import {
  KeyRound,
  LogIn,
  ShieldCheck,
  TriangleAlert,
  UserRound,
} from "lucide-react";
import { useEffect, useState, type FormEvent } from "react";
import { ApiError, api } from "../api";
import { ActionButton, BrandMark, SandArcs } from "../components/design-system";
import type { AdminAuthMode, AdminIdentity } from "../types";

const errorMessages: Record<string, string> = {
  access_denied: "当前统一身份没有管理权限，请切换为管理员身份。",
  failed: "统一身份认证未完成，请重新认证。",
  unavailable: "统一身份认证暂时不可用，请稍后重试。",
};

export function LoginPage({
  mode,
  onAuthenticated,
}: {
  mode: AdminAuthMode;
  onAuthenticated: (admin: AdminIdentity) => void;
}) {
  return mode === "local" ? (
    <LocalLoginPage onAuthenticated={onAuthenticated} />
  ) : (
    <OidcLoginPage />
  );
}

function OidcLoginPage() {
  const errorCode = new URLSearchParams(window.location.search).get(
    "sso_error",
  );
  const error = errorCode
    ? errorMessages[errorCode] || errorMessages.failed
    : "";
  const loginUrl = api.ssoLoginUrl({ forceLogin: Boolean(error) });

  useEffect(() => {
    if (!error) window.location.replace(loginUrl);
  }, [error, loginUrl]);

  return (
    <main className="relative flex min-h-screen items-center justify-center overflow-hidden bg-cream px-4 py-10">
      <SandArcs className="absolute -right-20 -top-24 w-[720px] max-w-[86vw] opacity-80" />
      <div className="absolute -bottom-40 -left-32 h-[420px] w-[420px] rounded-full border border-sand/70" />
      <div className="absolute -bottom-24 -left-16 h-[280px] w-[280px] rounded-full bg-peach/70" />
      <section className="admin-login-panel rise-in relative z-[1] w-full max-w-[430px] rounded-[24px] border border-sand-light/90 bg-paper/95 p-7 shadow-[0_28px_90px_-42px_rgba(46,42,37,0.35)] backdrop-blur-xl sm:p-9">
        <div className="flex items-center gap-3">
          <BrandMark size={42} />
          <div>
            <strong className="font-display text-[18px] font-medium tracking-wide text-ink">
              Easy SWU
            </strong>
            <p className="eyebrow mt-0.5 text-[8px] text-ink-faint">
              MANAGEMENT CONSOLE
            </p>
          </div>
        </div>
        <div className="mt-10">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-peach px-3 py-1 text-[11px] font-medium text-clay-deep">
            <ShieldCheck size={13} />
            统一身份认证
          </span>
          <h1 className="mt-4 font-display text-[28px] font-medium tracking-tight text-ink">
            {error ? "暂时无法进入" : "正在进入管理后台"}
          </h1>
          <p className="mt-2 text-[13px] leading-relaxed text-ink-soft">
            管理后台仅向已授权的统一身份开放。
          </p>
        </div>
        <div className="mt-8 space-y-5">
          {error && (
            <div
              className="flex items-start gap-2 rounded-xl border border-[#E8C9B8] bg-[#FBEEE8] p-3 text-[12px] text-destructive"
              role="alert"
            >
              <TriangleAlert size={15} className="mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}
          <ActionButton
            tone="dark"
            type="button"
            busy={!error}
            onClick={() => window.location.assign(loginUrl)}
            className="w-full py-3"
          >
            {errorCode === "access_denied" ? "切换统一身份" : "重新认证"}
          </ActionButton>
        </div>
        <div className="mt-7 flex items-start gap-2 border-t border-sand-light/70 pt-5 text-[11px] leading-relaxed text-ink-faint">
          <LogIn size={14} className="mt-0.5 shrink-0" />
          <span>认证完成后将自动返回此页面。</span>
        </div>
      </section>
    </main>
  );
}

function LocalLoginPage({
  onAuthenticated,
}: {
  onAuthenticated: (admin: AdminIdentity) => void;
}) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!username.trim() || !password || busy) return;
    setBusy(true);
    setError("");
    try {
      const session = await api.login(username.trim(), password);
      onAuthenticated(session.admin);
    } catch (caught) {
      setError(
        caught instanceof ApiError ? caught.message : "暂时无法连接管理服务。",
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="relative flex min-h-screen items-center justify-center overflow-hidden bg-cream px-4 py-10">
      <SandArcs className="absolute -right-20 -top-24 w-[720px] max-w-[86vw] opacity-80" />
      <div className="absolute -bottom-40 -left-32 h-[420px] w-[420px] rounded-full border border-sand/70" />
      <div className="absolute -bottom-24 -left-16 h-[280px] w-[280px] rounded-full bg-peach/70" />
      <section className="admin-login-panel rise-in relative z-[1] w-full max-w-[430px] rounded-[24px] border border-sand-light/90 bg-paper/95 p-7 shadow-[0_28px_90px_-42px_rgba(46,42,37,0.35)] backdrop-blur-xl sm:p-9">
        <div className="flex items-center gap-3">
          <BrandMark size={42} />
          <div>
            <strong className="font-display text-[18px] font-medium tracking-wide text-ink">
              Easy SWU
            </strong>
            <p className="eyebrow mt-0.5 text-[8px] text-ink-faint">
              MANAGEMENT CONSOLE
            </p>
          </div>
        </div>
        <div className="mt-10">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-peach px-3 py-1 text-[11px] font-medium text-clay-deep">
            <ShieldCheck size={13} />
            本地开发
          </span>
          <h1 className="mt-4 font-display text-[28px] font-medium tracking-tight text-ink">
            欢迎回来
          </h1>
          <p className="mt-2 text-[13px] leading-relaxed text-ink-soft">
            使用本地管理员账号进入开发环境。
          </p>
        </div>
        <form className="mt-8 space-y-5" onSubmit={submit}>
          <label className="block">
            <span className="eyebrow text-[9px] text-ink-faint">
              ADMIN · 管理员账号
            </span>
            <div className="mt-2 flex items-center gap-3 rounded-xl border border-sand-light bg-cream/60 px-4 py-3 transition focus-within:border-clay/50 focus-within:bg-paper">
              <UserRound size={16} className="text-ink-faint" />
              <input
                autoFocus
                autoComplete="username"
                value={username}
                onChange={(event) => setUsername(event.target.value)}
                placeholder="请输入管理员账号"
                className="w-full bg-transparent text-[13.5px] text-ink outline-none placeholder:text-ink-faint/60"
              />
            </div>
          </label>
          <label className="block">
            <span className="eyebrow text-[9px] text-ink-faint">
              PASSWORD · 管理员密码
            </span>
            <div className="mt-2 flex items-center gap-3 rounded-xl border border-sand-light bg-cream/60 px-4 py-3 transition focus-within:border-clay/50 focus-within:bg-paper">
              <KeyRound size={16} className="text-ink-faint" />
              <input
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                placeholder="请输入管理员密码"
                className="w-full bg-transparent text-[13.5px] text-ink outline-none placeholder:text-ink-faint/60"
              />
            </div>
          </label>
          {error && (
            <div
              className="flex items-start gap-2 rounded-xl border border-[#E8C9B8] bg-[#FBEEE8] p-3 text-[12px] text-destructive"
              role="alert"
            >
              <TriangleAlert size={15} className="mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}
          <ActionButton
            tone="dark"
            type="submit"
            busy={busy}
            disabled={!username.trim() || !password}
            className="w-full py-3"
          >
            进入管理后台
          </ActionButton>
        </form>
        <div className="mt-7 flex items-start gap-2 border-t border-sand-light/70 pt-5 text-[11px] leading-relaxed text-ink-faint">
          <LogIn size={14} className="mt-0.5 shrink-0" />
          <span>此登录方式只在本地开发环境启用。</span>
        </div>
      </section>
    </main>
  );
}
